import { useDevRoleDashboard } from '@/contexts/DevRoleDashboardContext';
import { useReminderContext, type ReminderItem } from '@/contexts/ReminderContext';
import { supabase } from '@/services/supabaseClient';
import {
  BellAlertIcon,
  ClipboardDocumentIcon,
  CodeBracketIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline';
import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { LuxModal, LuxModalBody, LuxModalFooter, LuxModalHeader } from '@/components/ui/modal';

const PAGE_FILE_MAP: Record<string, string> = {
  '/': 'HomeLuxori.tsx',
  '/dashboard': 'DashboardPage.tsx',
  '/clients': 'ClientsPage.tsx',
  '/inventaire': 'InventairePage.tsx',
  '/fournisseurs': 'FournisseursPage.tsx',
  '/articles': 'ArticlesPage.tsx',
  '/deliveries': 'DeliveriesPage.tsx',
  '/deliveries/preparation': 'DeliveryPreparationPage.tsx',
  '/business/bons-de-commande': 'BonsDeCommandePage.tsx',
  '/business/bons-de-commande/nouveau': 'OrderFormPage.tsx',
  '/business/bons-de-commande/edit': 'OrderFormPage.tsx',
  '/business/bons-de-commande/view': 'OrderFormPage.tsx',
  '/business/bons-de-commande2': 'BonsDeCommandePage2.tsx',
  '/business/bons-de-commande2/nouveau': 'OrderFormPage2.tsx',
  '/business/bons-de-commande2/edit': 'OrderFormPage2.tsx',
  '/business/bons-de-commande2/view': 'OrderFormPage2.tsx',
  '/business/factures': 'FacturesPage.tsx',
  '/business/factures/nouvelle': 'InvoiceFormPage.tsx',
  '/business/factures-acomptes': 'FacturesAcomptePage.tsx',
  '/business/notes-de-credit': 'CreditNotesPage.tsx',
  '/business/notes-de-credit/nouvelle': 'CreditNotesFormPage.tsx',
  '/business/notes-de-credit/edit': 'CreditNotesFormPage.tsx',
  '/business/notes-de-credit/view': 'CreditNotesFormPage.tsx',
  '/business/services-apres-vente': 'AfterSalesPage.tsx',
  '/business/services-apres-vente/edit': 'AfterSalesFormPage.tsx',
  '/business/services-apres-vente/view': 'AfterSalesFormPage.tsx',
  '/business/achats': 'AchatsPage.tsx',
  '/business/commandes-fournisseurs': 'SupplierOrdersPage.tsx',
  '/business/commandes-en-attente': 'PendingSupplierOrdersPage.tsx',
  '/business/paiements-fournisseurs': 'PaiementsPage.tsx',
  '/business/factures-a-payer': 'FacturesAPayerPage.tsx',
  '/devis': 'DevisPage.tsx',
  '/devis/nouveau': 'DevisFormPage.tsx',
  '/marketing-studio': 'MarketingStudioPage.tsx',
  '/marketing-studio/nouveau': 'MarketingStudioFormPage.tsx',
  '/admin': 'AdminPage.tsx',
  '/management': 'ManagementPage.tsx',
  '/settings': 'SettingsPage.tsx',
};

function getPageFile(pathname: string): string | null {
  // Exact match
  if (PAGE_FILE_MAP[pathname]) return PAGE_FILE_MAP[pathname];
  // Strip trailing UUID/id segment for dynamic routes: /business/bons-de-commande/edit/xxx → /business/bons-de-commande/edit
  const withoutId = pathname.replace(/\/[0-9a-f-]{36}$/, '');
  if (PAGE_FILE_MAP[withoutId]) return PAGE_FILE_MAP[withoutId];
  // Strip last segment generically
  const parentPath = pathname.replace(/\/[^/]+$/, '');
  if (PAGE_FILE_MAP[parentPath]) return PAGE_FILE_MAP[parentPath];
  return null;
}

function createTestReminder(): ReminderItem {
  const now = new Date();
  return {
    id: `test-reminder-dev-${Date.now()}`,
    title: 'Rappel de test (dev)',
    description: null,
    appointment_datetime: now.toISOString(),
    reminder_datetime: now.toISOString(),
    status: 'active',
    item_type: 'task',
  };
}

interface RoleOption {
  id: string;
  nom: string;
}

/**
 * Pied de page du layout principal.
 */
export function AppFooter() {
  const { setActiveReminders } = useReminderContext();
  const devRole = useDevRoleDashboard();
  const location = useLocation();
  const currentPageFile = import.meta.env.DEV ? getPageFile(location.pathname) : null;
  const [roleModalOpen, setRoleModalOpen] = useState(false);
  const [roles, setRoles] = useState<RoleOption[]>([]);
  const [selectedRoleId, setSelectedRoleId] = useState<string>('');

  const loadRoles = useCallback(async () => {
    const { data, error } = await supabase
      .from('roles')
      .select('id, nom')
      .order('nom', { ascending: true });
    if (!error && data) setRoles(data);
  }, []);

  useEffect(() => {
    if (roleModalOpen) {
      loadRoles();
      setSelectedRoleId(devRole?.devRoleIdOverride ?? '');
    }
  }, [roleModalOpen, loadRoles, devRole?.devRoleIdOverride]);

  useEffect(() => {
    if (import.meta.env.DEV && devRole?.devRoleIdOverride && roles.length === 0) loadRoles();
  }, [devRole?.devRoleIdOverride, loadRoles, roles.length]);

  const handleApplyRole = () => {
    devRole?.setDevRoleIdOverride(selectedRoleId || null);
    setRoleModalOpen(false);
  };

  const handleClearRole = () => {
    devRole?.setDevRoleIdOverride(null);
    setSelectedRoleId('');
    setRoleModalOpen(false);
  };

  return (
    <footer className="layout-footer py-[13.5px] border-t flex-shrink-0">
      <div className={`mx-auto px-4 sm:px-6 lg:px-8 ${import.meta.env.DEV ? '' : 'max-w-7xl'}`}>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 text-sm">
          <p className="text-center">
            © Luxori {new Date().getFullYear()} - Logiciel sous licence réalisé par{' '}
            <span className="font-semibold text-blue-400">GMC Information Technology</span> 2021-
            {new Date().getFullYear()}. Tous droits réservés.
          </p>
          {import.meta.env.DEV && (
            <>
              <button
                type="button"
                onClick={() => setActiveReminders([createTestReminder()])}
                className="flex items-center gap-1.5 px-2.5 py-1 rounded border border-amber-500/50 bg-amber-500/10 text-amber-700 dark:text-amber-400 text-xs font-medium hover:bg-amber-500/20 transition-colors"
                title="Déclencher une alarme de rappel de test"
              >
                <BellAlertIcon className="h-4 w-4" />
                Test rappel
              </button>
              <button
                type="button"
                onClick={() => setRoleModalOpen(true)}
                className="flex items-center gap-1.5 px-2.5 py-1 rounded border border-slate-500/50 bg-slate-500/10 text-slate-600 dark:text-slate-300 text-xs font-medium hover:bg-slate-500/20 transition-colors"
                title="Choisir un rôle pour tester la disposition des boutons du dashboard"
              >
                <UserCircleIcon className="h-4 w-4" />
                Rôle test
                {devRole?.devRoleIdOverride && (
                  <span className="opacity-80">
                    ({roles.find(r => r.id === devRole.devRoleIdOverride)?.nom ?? '…'})
                  </span>
                )}
              </button>
              {currentPageFile && (
                <span
                  className="flex items-center gap-1 px-2.5 py-1 rounded border border-emerald-500/50 bg-emerald-500/10 text-emerald-400 text-xs font-mono"
                  title={`Fichier source : src/pages/business/${currentPageFile}`}
                >
                  <CodeBracketIcon className="h-3.5 w-3.5" />
                  {currentPageFile}
                  <button
                    type="button"
                    onClick={() => navigator.clipboard.writeText(currentPageFile)}
                    className="ml-0.5 hover:text-emerald-200 transition-colors"
                    title="Copier le nom du fichier"
                  >
                    <ClipboardDocumentIcon className="h-3.5 w-3.5" />
                  </button>
                </span>
              )}
            </>
          )}
        </div>
      </div>

      {import.meta.env.DEV && devRole && (
        <LuxModal isOpen={roleModalOpen} onClose={() => setRoleModalOpen(false)} size="sm">
          <LuxModalHeader variant="default" onClose={() => setRoleModalOpen(false)}>
            Rôle pour test (dashboard)
          </LuxModalHeader>
          <LuxModalBody>
            <p className="text-sm text-muted mb-3">
              Choisissez un rôle pour afficher les boutons du tableau de bord comme pour ce rôle
              (sans changer de compte).
            </p>
            <label className="block text-sm font-medium text-ink mb-1">Rôle</label>
            <select
              value={selectedRoleId}
              onChange={e => setSelectedRoleId(e.target.value)}
              className="w-full rounded-md border border-default bg-surface px-3 py-2 text-sm text-ink focus:ring-2 focus:ring-token-focus focus:border-token-focus"
            >
              <option value="">— Aucun (mon rôle) —</option>
              {roles.map(r => (
                <option key={r.id} value={r.id}>
                  {r.nom}
                </option>
              ))}
            </select>
          </LuxModalBody>
          <LuxModalFooter>
            <button
              type="button"
              onClick={handleClearRole}
              className="px-3 py-1.5 text-sm border border-default rounded-md bg-surface text-ink hover:bg-panel"
            >
              Aucun (mon rôle)
            </button>
            <button
              type="button"
              onClick={handleApplyRole}
              className="px-3 py-1.5 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700"
            >
              Appliquer
            </button>
          </LuxModalFooter>
        </LuxModal>
      )}
    </footer>
  );
}
